// Fallback to production backend if environment variable is not set
const DEFAULT_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5000'
  : 'https://pulc-backend-4cafc4d07c39.herokuapp.com';
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || DEFAULT_BASE_URL;

class ReviewAPI {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    if (options.token) {
      config.headers.Authorization = `Bearer ${options.token}`;
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication
  async login(username, password) {
    return this.request('/api/login', {
      method: 'POST',
      body: JSON.stringify({ identifier: username, password }),
    });
  }

  async verifyToken(token) {
    return this.request('/api/verify-token', {
      method: 'GET',
      token,
    });
  }

  // Training essays management
  async getTrainingEssays(token, page = 1, pageSize = 20, subject = null) {
    let endpoint = `/api/training/essays?page=${page}&pageSize=${pageSize}`;
    if (subject) {
      endpoint += `&subject=${encodeURIComponent(subject)}`;
    }
    
    return this.request(endpoint, {
      method: 'GET',
      token,
    });
  }

  async getTrainingEssay(token, trainingId) {
    return this.request(`/api/training/essays/${trainingId}`, {
      method: 'GET',
      token,
    });
  }

  async gradeTrainingEssay(token, trainingId) {
    return this.request(`/api/training/essays/${trainingId}/grade`, {
      method: 'POST',
      token,
    });
  }

  // Feedback editing (reusing existing endpoints)
  async updateFeedback(token, taskId, operation, feedbackKey, feedbackContent, newFeedbackType = null) {
    return this.request(`/api/results/${taskId}/update_feedback`, {
      method: 'POST',
      token,
      body: JSON.stringify({
        operation,
        feedbackKey,
        feedbackContent,
        newFeedbackType
      }),
    });
  }

  async addFeedback(token, taskId, paraIndex, feedbackContent, feedbackType) {
    return this.request(`/api/results/${taskId}/update_feedback`, {
      method: 'POST',
      token,
      body: JSON.stringify({
        operation: 'add',
        feedbackKey: `${paraIndex}`,
        feedbackContent,
        newFeedbackType: feedbackType
      }),
    });
  }

  async updateGrade(token, taskId, gradeText) {
    return this.request(`/api/results/${taskId}/update_grade`, {
      method: 'POST',
      token,
      body: JSON.stringify({
        grade_text: gradeText
      }),
    });
  }

  async updateTeacherFeedback(token, taskId, teacherFeedback) {
    return this.request(`/api/results/${taskId}/update_teacher_feedback`, {
      method: 'POST',
      token,
      body: JSON.stringify({
        teacher_feedback: teacherFeedback
      }),
    });
  }

  // Comment ratings
  async rateComment(token, taskId, paraIndex, fbIndex, rating) {
    return this.request(`/api/results/${taskId}/rate_comment`, {
      method: 'POST',
      token,
      body: JSON.stringify({
        para_index: paraIndex,
        fb_index: fbIndex,
        rating
      }),
    });
  }

  // Admin endpoints for essay assignment
  async getQAFeedbackEssays(token, page = 1, pageSize = 20, filters = {}) {
    let endpoint = `/api/admin/qa-feedback?page=${page}&pageSize=${pageSize}`;
    
    if (filters.subject) {
      endpoint += `&subject=${encodeURIComponent(filters.subject)}`;
    }
    if (filters.category) {
      endpoint += `&category=${encodeURIComponent(filters.category)}`;
    }
    if (filters.user_id) {
      endpoint += `&user_id=${encodeURIComponent(filters.user_id)}`;
    }
    
    return this.request(endpoint, {
      method: 'GET',
      token,
    });
  }

  async getTeachers(token, search = '') {
    let endpoint = `/api/admin/teachers`;
    if (search) {
      endpoint += `?search=${encodeURIComponent(search)}`;
    }
    
    return this.request(endpoint, {
      method: 'GET',
      token,
    });
  }

  async getValidCategories(token) {
    return this.request('/api/admin/categories', {
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
      },
    });
  }

  async getTrainingCategories(token) {
    return this.request('/api/admin/training-categories', {
      method: 'GET',
      token,
    });
  }

  async checkTeacherAccess(token) {
    return this.request('/user/teacher-access', {
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
      },
    });
  }

  async suggestGradingCategory(token, trainingCategory) {
    return this.request('/api/admin/suggest-grading-category', {
      method: 'POST',
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        training_category: trainingCategory
      }),
    });
  }

  async convertToTraining(token, qaFeedbackId, teacherId, studentName, studentEmail, originalGrade, gradingCategory = null, trainingCategory = null) {
    return this.request('/api/admin/convert-to-training', {
      method: 'POST',
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        qa_feedback_id: qaFeedbackId,
        teacher_id: teacherId,
        student_name: studentName,
        student_email: studentEmail,
        original_grade: originalGrade,
        grading_category: gradingCategory,
        training_category: trainingCategory
      }),
    });
  }

  async bulkConvertToTraining(token, conversions) {
    return this.request('/api/admin/bulk-convert-to-training', {
      method: 'POST',
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        conversions: conversions
      }),
    });
  }

  async getTrainingAssignments(token, page = 1, pageSize = 20, filters = {}) {
    let endpoint = `/api/admin/training-assignments?page=${page}&pageSize=${pageSize}`;
    
    if (filters.teacher) {
      endpoint += `&teacher=${encodeURIComponent(filters.teacher)}`;
    }
    if (filters.subject) {
      endpoint += `&subject=${encodeURIComponent(filters.subject)}`;
    }
    
    return this.request(endpoint, {
      method: 'GET',
      token,
    });
  }

  // Re-grading functionality
  async resetTrainingGrading(token, trainingId) {
    return this.request(`/api/admin/training/${trainingId}/reset-grading`, {
      method: 'POST',
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  }

  async regradeTrainingEssay(token, trainingId) {
    return this.request(`/api/admin/training/${trainingId}/regrade`, {
      method: 'POST',
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  }

  async regradeQAFeedback(token, qaId) {
    return this.request(`/api/admin/qa-feedback/${qaId}/regrade`, {
      method: 'POST',
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  }

  async bulkRegrade(token, trainingIds = [], qaFeedbackIds = []) {
    return this.request('/api/admin/bulk-regrade', {
      method: 'POST',
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        training_ids: trainingIds,
        qa_feedback_ids: qaFeedbackIds
      }),
    });
  }

  async getTaskStatus(token, taskId) {
    return this.request(`/api/task-status/${taskId}`, {
      method: 'GET',
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
      },
    });
  }

  async reassignTrainingEssay(token, trainingId, teacherId) {
    return this.request(`/api/admin/training/${trainingId}/reassign`, {
      method: 'POST',
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ teacher_id: teacherId }),
    });
  }
}

export const reviewAPI = new ReviewAPI(); 